'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function StandardDeviationCalculator() {
  const t = useTranslations("tools.standardDeviationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [dataInput, setDataInput] = useState('10, 12, 23, 23, 16, 23, 21, 16');
  const [dataType, setDataType] = useState<'population' | 'sample'>('population');
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const parseData = (input: string): number[] => {
    const cleaned = input.replace(/[,\s\n\r]+/g, ',');
    const values = cleaned.split(',')
      .map(v => v.trim())
      .filter(v => v !== '')
      .map(v => parseFloat(v));

    if (values.some(isNaN)) {
      throw new Error(t('errorInvalidNumbers'));
    }

    if (values.length < 2) {
      throw new Error(t('errorMinimumValues'));
    }

    return values;
  };

  const calculateMean = (values: number[]): number => {
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  };

  const calculateVariance = (values: number[], mean: number, isSample: boolean): number => {
    const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
    const sumSquaredDiffs = squaredDiffs.reduce((sum, val) => sum + val, 0);
    const divisor = isSample ? values.length - 1 : values.length;
    return sumSquaredDiffs / divisor;
  };

  const calculateStandardDeviation = (variance: number): number => {
    return Math.sqrt(variance);
  };

  const formatNumber = (num: number, decimals: number = 6): string => {
    if (Number.isInteger(num)) return num.toString();
    const formatted = num.toFixed(decimals);
    return parseFloat(formatted).toString();
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const values = parseData(dataInput);
      const isSample = dataType === 'sample';

      const sum = values.reduce((acc, val) => acc + val, 0);
      const count = values.length;
      const mean = calculateMean(values);
      const variance = calculateVariance(values, mean, isSample);
      const stdDev = calculateStandardDeviation(variance);
      const cv = mean !== 0 ? (stdDev / Math.abs(mean)) * 100 : 0;

      const sortedValues = [...values].sort((a, b) => a - b);

      setResults({
        stdDev: formatNumber(stdDev),
        variance: formatNumber(variance),
        mean: formatNumber(mean),
        sum: formatNumber(sum),
        count,
        cv: formatNumber(cv, 2),
        dataValues: sortedValues.join(', ')
      });
    } catch (err: any) {
      setError(err.message);
      setResults(null);
    }
  };

  const handleClear = () => {
    setDataInput('');
    setDataType('population');
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1d4ed8',
    purple: '#7c3aed',
    yellow: '#fef3c7',
    yellowDark: '#92400e',
    orange: '#f59e0b',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 20px rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '900px',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const
    },
    {},
    {},
    {}
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.375rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: colors.textPrimary,
    fontSize: '0.95rem'
  };

  const textareaStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontFamily: "'Courier New', monospace",
      transition: 'all 0.3s ease',
      resize: 'vertical' as const,
      minHeight: '120px',
      boxSizing: 'border-box' as const
    },
    { padding: '0.875rem', fontSize: '0.95rem' },
    { padding: '0.95rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const radioGroupStyle = getResponsiveStyle(
    {
      display: 'flex',
      alignItems: 'center',
      background: '#f8fafc',
      borderRadius: '0.5rem'
    },
    { flexDirection: 'column' as const, alignItems: 'flex-start', gap: '1rem', padding: '1rem' },
    { flexDirection: 'row' as const, gap: '1.5rem', padding: '1rem' },
    { flexDirection: 'row' as const, gap: '2rem', padding: '1rem' }
  );

  const radioOptionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer'
  };

  const radioInputStyle = {
    width: '1.2rem',
    height: '1.2rem',
    cursor: 'pointer',
    accentColor: colors.blue
  };

  const radioLabelStyle = {
    margin: 0,
    cursor: 'pointer',
    fontWeight: 500
  };

  const helpTextStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.85rem',
    color: '#64748b',
    marginTop: '0.5rem'
  };

  const helpIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.2rem',
    height: '1.2rem',
    background: colors.blue,
    color: 'white',
    borderRadius: '50%',
    fontSize: '0.75rem',
    fontWeight: 'bold' as const,
    cursor: 'help'
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', marginTop: '2rem' },
    { flexDirection: 'column' as const, gap: '0.75rem' },
    { flexDirection: 'row' as const, gap: '1rem' },
    { flexDirection: 'row' as const, gap: '1rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.purple} 100%)`,
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.95rem 1.75rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.95rem 1.75rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1rem' }
  );

  const errorMessageStyle = {
    padding: '1rem',
    background: '#fee2e2',
    borderLeft: '4px solid #ef4444',
    borderRadius: '0.5rem',
    color: '#991b1b',
    marginTop: '1rem',
    display: error ? 'block' : 'none'
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderTop: `3px solid ${colors.orange}`,
      display: results ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    {
      margin: '0 0 1.5rem 0',
      color: colors.yellowDark,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.3rem' }
  );

  const resultsGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
  );

  const resultCardStyle = {
    background: 'white',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  };

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px'
  };

  const resultValueStyle = {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: colors.textPrimary,
    fontFamily: "'Courier New', monospace"
  };

  const resultValuePrimaryStyle = {
    ...resultValueStyle,
    color: colors.blue,
    fontSize: '1.8rem'
  };

  const dataSummaryStyle = {
    background: 'white',
    padding: '1rem',
    borderRadius: '0.75rem',
    marginTop: '1rem'
  };

  const dataSummaryTitleStyle = {
    fontWeight: 600,
    color: '#475569',
    marginBottom: '0.5rem',
    fontSize: '0.9rem'
  };

  const dataValuesStyle = {
    fontFamily: "'Courier New', monospace",
    color: '#64748b',
    fontSize: '0.85rem',
    lineHeight: 1.6
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem'
  };

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: `2px solid ${colors.accent}`,
    transition: 'all 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>📊 {t('toolTitle')}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="data-input">
                {t('dataInputLabel')}
              </label>
              <textarea
                style={textareaStyle}
                id="data-input"
                placeholder={t('dataInputPlaceholder')}
                value={dataInput}
                onChange={(e) => setDataInput(e.target.value)}
                required
              />
              <div style={helpTextStyle}>
                <span style={helpIconStyle}>?</span>
                {t('dataInputHelp')}
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('dataTypeLabel')}</label>
              <div style={radioGroupStyle}>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="population"
                    name="dataType"
                    value="population"
                    checked={dataType === 'population'}
                    onChange={(e) => setDataType(e.target.value as 'population' | 'sample')}
                    style={radioInputStyle}
                  />
                  <label htmlFor="population" style={radioLabelStyle}>
                    {t('populationOption')}
                  </label>
                </div>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="sample"
                    name="dataType"
                    value="sample"
                    checked={dataType === 'sample'}
                    onChange={(e) => setDataType(e.target.value as 'population' | 'sample')}
                    style={radioInputStyle}
                  />
                  <label htmlFor="sample" style={radioLabelStyle}>
                    {t('sampleOption')}
                  </label>
                </div>
              </div>
              <div style={helpTextStyle}>
                <span style={helpIconStyle}>?</span>
                {t('dataTypeHelp')}
              </div>
            </div>

            <div style={errorMessageStyle}>
              {error}
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateBtnStyle}>
                {t('calculateButton')}
              </button>
              <button type="button" style={clearBtnStyle} onClick={handleClear}>
                {t('clearButton')}
              </button>
            </div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📈 {t('resultsTitle')}</h3>
              <div style={resultsGridStyle}>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('standardDeviationLabel')}</span>
                  <span style={resultValuePrimaryStyle}>{results.stdDev}</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('varianceLabel')}</span>
                  <span style={resultValueStyle}>{results.variance}</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('meanLabel')}</span>
                  <span style={resultValueStyle}>{results.mean}</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('sumLabel')}</span>
                  <span style={resultValueStyle}>{results.sum}</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('countLabel')}</span>
                  <span style={resultValueStyle}>{results.count}</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('cvLabel')}</span>
                  <span style={resultValueStyle}>{results.cv}%</span>
                </div>
              </div>
              <div style={dataSummaryStyle}>
                <div style={dataSummaryTitleStyle}>{t('dataSetLabel')}</div>
                <div style={dataValuesStyle}>{results.dataValues}</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Introduction */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>

        <h3 style={h3Style}>{t('applicationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('financeTitle')}</strong> {t('financeDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('qualityControlTitle')}</strong> {t('qualityControlDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('researchTitle')}</strong> {t('researchDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('healthcareTitle')}</strong> {t('healthcareDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('sportsTitle')}</strong> {t('sportsDesc')}</li>
        </ul>

        <h3 style={h3Style}>{t('guidelinesTitle')}</h3>
        <p style={paragraphStyle}>{t('guidelinesParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('sampleVsPopTitle')}</strong> {t('sampleVsPopDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('sampleSizeTitle')}</strong> {t('sampleSizeDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('cvTitle')}</strong> {t('cvDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('outliersTitle')}</strong> {t('outliersDesc')}</li>
        </ul>
      </section>

      {/* Understanding the Formulas */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('formulasTitle')}</h2>

        <h3 style={h3Style}>{t('populationFormulaTitle')}</h3>
        <p style={paragraphStyle}>{t('populationFormulaDesc')}</p>
        <p style={{...paragraphStyle, fontFamily: "'Courier New', monospace", background: '#f8fafc', padding: '1rem', borderRadius: '0.5rem'}}>
          σ = √[Σ(x - μ)² / N]
        </p>

        <h3 style={h3Style}>{t('sampleFormulaTitle')}</h3>
        <p style={paragraphStyle}>{t('sampleFormulaDesc')}</p>
        <p style={{...paragraphStyle, fontFamily: "'Courier New', monospace", background: '#f8fafc', padding: '1rem', borderRadius: '0.5rem'}}>
          s = √[Σ(x - x̄)² / (n-1)]
        </p>

        <h3 style={h3Style}>{t('varianceTitle')}</h3>
        <p style={paragraphStyle}>{t('varianceDesc')}</p>

        <h3 style={h3Style}>{t('cvFormulaTitle')}</h3>
        <p style={paragraphStyle}>{t('cvFormulaDesc')}</p>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('accuracyTitle')}</strong> {t('accuracyDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('contextTitle')}</strong> {t('contextDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('checkOutliersTitle')}</strong> {t('checkOutliersDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('combineMetricsTitle')}</strong> {t('combineMetricsDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('normalDistTitle')}</strong> {t('normalDistDesc')}</li>
        </ul>

        <h3 style={h3Style}>{t('limitationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('limitation1')}</li>
          <li style={listItemStyle}>{t('limitation2')}</li>
          <li style={listItemStyle}>{t('limitation3')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={h3Style}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={h3Style}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={h3Style}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={h3Style}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>

        <h3 style={h3Style}>{t('faq6Question')}</h3>
        <p style={paragraphStyle}>{t('faq6Answer')}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/standard-deviation-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.standarddeviationcalculator.io/applications" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.graphcalc.com/standard-deviation-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://en.wikipedia.org/wiki/Standard_deviation" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.geeksforgeeks.org/maths/real-life-applications-of-standard-deviation/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('ref5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}
